<template>
    <span v-if="isAi && !isBackgroundImage && showAiHint" class="bk-ai-image">
        <picture
            ref="imageElement"
            :data-iesrc="defaultImageUrl"
            :data-id="id"
            :data-image-alt="cleanImageAlt"
            :data-picture-class="resolvedPictureClass"
            :data-alt="resolvedAlt"
            :data-title="resolvedTitle"
            :data-fetchpriority="fetchPriority"
            :data-height="getHeight()"
            :data-width="getWidth()"
            :id="uuid">
            <slot name="additionalimages"></slot>
            <source v-if="avifSourceUrl" :srcset="avifSourceUrl" type="image/avif">
            <source :srcset="defaultImageUrl" :type="mimeType(defaultImageUrl)">
            <source v-if="defaultImageUrl !== imageUrl" :srcset="imageUrl" :type="mimeType(imageUrl)">
            <source v-if="fallbackUrl" :srcset="fallbackUrl" :type="mimeType(fallbackUrl)">
            <img v-if="receivedImageExtension === 'tif'" :src="defaultImageUrl" :alt="resolvedAlt" :title="resolvedTitle" :class="resolvedPictureClass" :data-managed-image-classes="resolvedPictureClass" :height="getHeight()" :width="getWidth()" :fetchpriority="fetchPriority" type="image/tiff" class="mw-100 h-auto">
            <img v-else-if="isImageReady" :alt="resolvedAlt" :title="resolvedTitle" :class="resolvedPictureClass" :data-managed-image-classes="resolvedPictureClass" :height="getHeight()" :width="getWidth()" :fetchpriority="fetchPriority" class="mw-100 h-auto">
        </picture>
        <ai-image-hint />
    </span>
    <picture
        v-else-if="!isBackgroundImage"
        ref="imageElement"
        :data-iesrc="defaultImageUrl"
        :data-id="id"
        :data-image-alt="cleanImageAlt"
        :data-picture-class="resolvedPictureClass"
        :data-alt="resolvedAlt"
        :data-title="resolvedTitle"
        :data-fetchpriority="fetchPriority"
        :data-height="getHeight()"
        :data-width="getWidth()"
        :id="uuid">
        <slot name="additionalimages"></slot>
        <source v-if="avifSourceUrl" :srcset="avifSourceUrl" type="image/avif">
        <source :srcset="defaultImageUrl" :type="mimeType(defaultImageUrl)">
        <source v-if="defaultImageUrl !== imageUrl" :srcset="imageUrl" :type="mimeType(imageUrl)">
        <source v-if="fallbackUrl" :srcset="fallbackUrl" :type="mimeType(fallbackUrl)">
        <img v-if="receivedImageExtension === 'tif'" :src="defaultImageUrl" :alt="resolvedAlt" :title="resolvedTitle" :class="resolvedPictureClass" :data-managed-image-classes="resolvedPictureClass" :height="getHeight()" :width="getWidth()" :fetchpriority="fetchPriority" type="image/tiff" class="mw-100 h-auto">
        <img v-else-if="isImageReady" :alt="resolvedAlt" :title="resolvedTitle" :class="resolvedPictureClass" :data-managed-image-classes="resolvedPictureClass" :height="getHeight()" :width="getWidth()" :fetchpriority="fetchPriority" class="mw-100 h-auto">
    </picture>

    <div v-else ref="imageElement" :data-background-image="defaultImageUrl || fallbackUrl" :class="[resolvedPictureClass, { 'bk-ai-hint-host': isAi }]">
        <slot></slot>
        <ai-image-hint v-if="isAi" />
    </div>
</template>

<script>
import lozad from "../../plugins/lozad";
import { getImageMetadata, parseImageText, syncImageAttributes } from "../../helper/imageMetadata";
import { detectAvif, detectWebP } from "../../helper/featureDetect";
import { type } from "jquery";

const mime = require("mime-types");

export default {
    props:
    {
        convertImage: {
            type: Boolean,
            default: true
        },
        imageUrl: {
            type: String,
            default: null
        },
        fallbackUrl: {
            type: String,
            default: null
        },
        isBackgroundImage: {
            type: Boolean,
            default: false
        },
        pictureClass: {
            type: String,
            default: null
        },
        alt: {
            type: String,
            default: null
        },
        imageFlags: {
            type: Array,
            default: () => []
        },
        fallbackAlt: {
            type: String,
            default: ""
        },
        imageAlt: {
            type: String,
            default: null
        },
        title: {
            type: String,
            default: null
        },
        height: {
            type: Number | null,
            default: null
        },
        width: {
            type: Number | null,
            default: null
        },
        id: {
            type: Number,
            default: 0
        },
        index: {
            type: Number,
            default: -1
        },
        showAiHint: {
            type: Boolean,
            default: true
        }
    },
    data()
    {
        return {
            isImageReady: false,
            imageConversionEnabled: App.config.log.modernImagesConversion,
            receivedImageExtension: null,
            browserSupportedImgExtension: null,
            defaultImageUrl: this.imageUrl,
            avifSupported: false,
            avifExtension: "avif",
            webpSupported: false,
            webpExtension: "webp",
            uuid: null,
            imgRegex: /.?(\.\w+)(?:$|\?)/
        };
    },
    mounted()
    {
        this.generateUuid();

        detectAvif((avifSupported) => {
            this.avifSupported = avifSupported;

            if (avifSupported)
            {
                this.$nextTick(() => {
                    this.observeImage();
                });
                this.propagateImageFormat();
            }

            if (!avifSupported)
            {
                detectWebP((webpSupported) => {
                    this.webpSupported = webpSupported;

                    if (webpSupported)
                    {
                        this.$nextTick(() => {
                            this.observeImage();
                        });
                        this.propagateImageFormat();
                    }
                });
            }
        });
    },
    updated()
    {
        this.observeImage();
        this.syncRenderedImage();
    },
    beforeDestroy()
    {
        this._imageObserver?.observer?.disconnect();
    },
    watch:
    {
        defaultImageUrl()
        {
            this.$nextTick(() => {
                const element = this.$refs.imageElement;

                if (!element)
                {
                    return;
                }
                element.setAttribute("data-loaded", "false");
                if (!this.isBackgroundImage)
                {
                    element.querySelector("img")?.remove();
                }
                lozad(element, {
                    loaded(el)
                    {
                        el.classList.remove("lozad");
                    }
                }).triggerLoad(element);
            });
        },
        imageUrl()
        {
            this.$nextTick(() => {
                this.propagateImageFormat();
            });
        }
    },
    computed:
    {
        convertedImageUrl()
        {
            return `${this.imageUrl}.${this.browserSupportedImgExtension}`;
        },
        avifSourceUrl()
        {
            if (!this.convertImage || !this.imageUrl || !/\/item\/images\//.test(this.imageUrl))
            {
                return null;
            }

            if (/\.avif(?:$|\?)/i.test(this.imageUrl))
            {
                return this.imageUrl;
            }

            if (/\.(jpe?g|png|webp)(?:$|\?)/i.test(this.imageUrl))
            {
                return `${this.imageUrl}.avif`;
            }

            return null;
        },
        imageMetadata()
        {
            return getImageMetadata(this.alt || this.imageAlt, this.title,
                this.imageFlags.concat(parseImageText(this.imageAlt).flags), this.fallbackAlt);
        },
        cleanImageAlt()
        {
            return parseImageText(this.imageAlt).text;
        },
        resolvedAlt()
        {
            return this.imageMetadata.alt;
        },
        resolvedTitle()
        {
            return this.imageMetadata.title;
        },
        resolvedPictureClass()
        {
            return [this.pictureClass, this.imageMetadata.classes].filter(Boolean).join(" ");
        },
        isAi()
        {
            return this.imageMetadata.isAi;
        },
        fetchPriority()
        {
            return this.index === 0 ? "high" : null;
        }
    },
    methods:
    {
        observeImage()
        {
            const element = this.$refs.imageElement;

            if (!this.isImageReady || !element || element === this._observedImageElement)
            {
                return;
            }
            this._imageObserver?.observer?.disconnect();
            this._observedImageElement = element;
            if (!this.isBackgroundImage)
            {
                element.classList.add("lozad");
            }
            this._imageObserver = lozad(element);
            this._imageObserver.observe();
        },
        syncRenderedImage()
        {
            if (!this.isBackgroundImage)
            {
                const element = this.$refs.imageElement;
                const img = element?.querySelector("img");

                if (img)
                {
                    syncImageAttributes(element, img);
                }
            }
        },
        mimeType(url)
        {
            return mime.lookup(url);
        },
        getHeight()
        {
            if (this.height && this.height > 0 && this.defaultImageUrl.includes("/full/"))
            {
                return this.height;
            }
            return undefined;
        },
        getWidth()
        {
            if (this.width && this.width > 0 && this.defaultImageUrl.includes("/full/"))
            {
                return this.width;
            }
            return undefined;
        },
        propagateImageFormat()
        {
            this.setReceivedImageExtension();
            this.setBrowserSupportedImageExtension();
            this.setDefaultImageUrl();
            this.isImageReady = true;
        },
        setReceivedImageExtension()
        {
            const matches = this.imageUrl?.match(this.imgRegex);

            if (matches) this.receivedImageExtension = matches[1].split(".").pop();
        },
        setBrowserSupportedImageExtension()
        {
            if (this.avifSupported)
            {
                this.browserSupportedImgExtension = this.avifExtension;
                return;
            }

            if (this.webpSupported)
            {
                this.browserSupportedImgExtension = this.webpExtension;
                return;
            }

            this.browserSupportedImgExtension = this.receivedImageExtension !== this.avifExtension && this.receivedImageExtension !== this.webpExtension
                ? this.receivedImageExtension
                : "jpeg";
        },
        setDefaultImageUrl()
        {
            if (this.imageShouldBeConverted())
            {
                if (this.receivedImageExtension === this.avifExtension)
                {
                    this.defaultImageUrl = this.browserSupportedImgExtension === this.avifExtension
                        ? this.imageUrl
                        : this.convertedImageUrl;
                    return;
                }
                if (this.receivedImageExtension === this.webpExtension)
                {
                    if (this.browserSupportedImgExtension === this.avifExtension)
                    {
                        this.defaultImageUrl = this.convertedImageUrl;
                        return;
                    }
                    if (this.browserSupportedImgExtension === this.webpExtension)
                    {
                        this.defaultImageUrl = this.imageUrl;
                        return;
                    }

                    this.defaultImageUrl = this.imageUrl;
                    return;
                }

                // convert anything other than avif or webp into browser supported format.
                this.defaultImageUrl = this.convertedImageUrl;
                return;
            }
            this.defaultImageUrl = this.imageUrl || this.fallbackUrl;
        },
        imageShouldBeConverted()
        {
            const validConversionExtensions = ["jpg", "JPG", "jpeg", "JPEG", "png", "PNG", "webp"];

            return this.convertImage
                && this.imageConversionEnabled
                && /\/item\/images\//.test(this.imageUrl)
                && this.browserSupportedImgExtension !== this.receivedImageExtension
                && validConversionExtensions.includes(this.receivedImageExtension);
        },
        generateUuid()
        {
            this.uuid = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        }
    }
};
</script>
